// ─────────────────────────────────────────────
//  ProfileOverview.jsx
//  Connected to: ProfileLogic.js, Profile.css
//  Used by: Profile.jsx
// ─────────────────────────────────────────────

import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "./Profile";
import { StatCard } from "./Profile";
import { formatCurrency, MEMBER_HELP_CONTACT, MEMBER_HELP_TEXT, splitSvcFeeGST } from "./ProfileLogic.js";
import { TrackingModal, getDecisionConfig } from "./VisaTracking.jsx";
import { generateReceiptHTML } from "../Products/Esim/Edata.js";
import receiptLogo from "../../../assets/PNG/RecieptLogo.png";

function ServiceFeeTooltip({ svcFee }) {
  const { base, gst } = splitSvcFeeGST(svcFee);
  return (
    <div className="ProfileFeeInfoWrap" style={{ display: "inline-flex", verticalAlign: "middle", marginLeft: 4 }}>
      <span className="ProfileFeeInfoIcon" style={{ cursor: "pointer", fontSize: 10, width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontWeight: 700 }}>i</span>
      <div className="ProfileFeeTooltip ProfileFeeTooltip--left">
        <div className="ProfileFeeTooltipRow">
          <span>Base Fee: </span>
          <span>₹{base.toLocaleString("en-IN")}</span>
        </div>
        <div className="ProfileFeeTooltipRow">
          <span>GST (18%): </span>
          <span>₹{gst.toLocaleString("en-IN")}</span>
        </div>
      </div>
    </div>
  );
}

function ConvFeeTooltip({ baseFee, gst }) {
  return (
    <div className="ProfileFeeInfoWrap" style={{ display: "inline-flex", verticalAlign: "middle", marginLeft: 4 }}>
      <span className="ProfileFeeInfoIcon" style={{ cursor: "pointer", fontSize: 10, width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontWeight: 700 }}>i</span>
      <div className="ProfileFeeTooltip ProfileFeeTooltip--left">
        <div className="ProfileFeeTooltipRow">
          <span>Base conv. fee</span>
          <span>₹{baseFee.toLocaleString("en-IN")}</span>
        </div>
        <div className="ProfileFeeTooltipRow">
          <span>GST (18%)</span>
          <span>₹{gst.toLocaleString("en-IN")}</span>
        </div>
      </div>
    </div>
  );
}

const downloadReceiptInner = async (app, userEmail, userRole) => {
  try {
    const { jsPDF } = await import("jspdf");
    const html2canvas = (await import("html2canvas")).default;
    const toBase64 = async (url) => {
      const resp = await fetch(url);
      const blob = await resp.blob();
      return new Promise((r) => {
        const reader = new FileReader();
        reader.onloadend = () => r(reader.result);
        reader.readAsDataURL(blob);
      });
    };
    let logoSrc = null;
    try { logoSrc = await toBase64(receiptLogo); } catch { logoSrc = null; }

    const count = app.applicants?.length ?? 1;
    const primary = app.applicants?.[0] || {};
    const discAmt = (app.discountAmount || 0) + (app.walletDisc || 0);
    const svcTotal = (app.svcFee || 0) * count;
    const protectTotal = app.protectFee || 0;
    const govTotal = (app.govFee || 0) * count;
    const convTotal = app.convenienceFee || 0;
    const expressTotal = app.expressFee || 0;
    const subtotal = govTotal + svcTotal + protectTotal;
    const total = subtotal + convTotal + expressTotal - discAmt;

    const pi = app.paymentInfo || {};
    let payMethod = "upi";
    if (pi.cardType) payMethod = "card";
    else if (pi.bankName) payMethod = "netbanking";
    else if (pi.upiId) payMethod = "upi";

    const item = {
      label: `Visa Application \u2014 ${app.countryName}`,
      countryName: app.countryName || "",
      price: subtotal,
      data: `${count} Applicant(s)`,
      validity: 1,
      type: "service",
    };

    const html = generateReceiptHTML({
      item, quantity: count, payMethod,
      billingDetails: {
        firstName: [primary.firstName, primary.lastName].filter(Boolean).join(" ") || "",
        lastName: "",
        email: userEmail || app.applicantUserName || "",
        country: app.userCountry || "India",
        whatsapp: "",
        address: "",
        city: "",
        pincode: "",
        accountType: userRole ? `${userRole.charAt(0).toUpperCase() + userRole.slice(1)} Account` : "",
        companyName: app.companyName || "",
      },
      orderId: app.applicationId,
      currency: "INR",
      appliedDiscount: discAmt,
      logoSrc, pdf: true, inr: true,
      upiId: pi.upiId || "",
      bank: pi.bankName || "",
      card: { number: pi.cardNumber || "" },
      simType: "",
      applicants: app.applicants || [],
      authorisedName: app.authorisedName,
      authorisedId: app.authorisedId,
      authorisedPhone: app.authorisedPhone,
      authorisedCountryCode: app.authorisedCountryCode,
      overrides: {
        subtotal,
        tax: 0,
        convFee: convTotal,
        total,
      },
    });

    const container = document.createElement("div");
    container.innerHTML = html;
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = "210mm";
    container.style.background = "#f5f9ff";
    document.body.appendChild(container);
    const el = container.querySelector(".Receipt-card") || container.firstElementChild;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff" });
    document.body.removeChild(container);

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    pdf.save(`TheVisaCo-Receipt-${app.applicationId}.pdf`);
  } catch (err) {
    console.error("PDF generation failed:", err);
  }
};

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill="#fff" d="M4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5c5.1,0,9.8,2,13.4,5.6	C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19c0,0,0,0,0,0h0c-3.2,0-6.3-0.8-9.1-2.3L4.9,43.3z"></path><path fill="#fff" d="M4.9,43.8c-0.1,0-0.3-0.1-0.4-0.1c-0.1-0.1-0.2-0.3-0.1-0.5L7,33.5c-1.6-2.9-2.5-6.2-2.5-9.6	C4.5,13.2,13.3,4.5,24,4.5c5.2,0,10.1,2,13.8,5.7c3.7,3.7,5.7,8.6,5.7,13.8c0,10.7-8.7,19.5-19.5,19.5c-3.2,0-6.3-0.8-9.1-2.3	L5,43.8C5,43.8,4.9,43.8,4.9,43.8z"></path><path fill="#cfd8dc" d="M24,5c5.1,0,9.8,2,13.4,5.6C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19h0c-3.2,0-6.3-0.8-9.1-2.3	L4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5 M24,43L24,43L24,43 M24,43L24,43L24,43 M24,4L24,4C13,4,4,13,4,24	c0,3.4,0.8,6.7,2.5,9.6L3.9,43c-0.1,0.3,0,0.7,0.3,1c0.2,0.2,0.4,0.3,0.7,0.3c0.1,0,0.2,0,0.3,0l9.7-2.5c2.8,1.5,6,2.2,9.2,2.2	c11,0,20-9,20-20c0-5.3-2.1-10.4-5.8-14.1C34.4,6.1,29.4,4,24,4L24,4z"></path><path fill="#40c351" d="M35.2,12.8c-3-3-6.9-4.6-11.2-4.6C15.3,8.2,8.2,15.3,8.2,24c0,3,0.8,5.9,2.4,8.4L11,33l-1.6,5.8	l6-1.6l0.6,0.3c2.4,1.4,5.2,2.2,8,2.2h0c8.7,0,15.8-7.1,15.8-15.8C39.8,19.8,38.2,15.8,35.2,12.8z"></path><path fill="#fff" fillRule="evenodd" d="M19.3,16c-0.4-0.8-0.7-0.8-1.1-0.8c-0.3,0-0.6,0-0.9,0s-0.8,0.1-1.3,0.6c-0.4,0.5-1.7,1.6-1.7,4s1.7,4.6,1.9,4.9s3.3,5.3,8.1,7.2c4,1.6,4.8,1.3,5.7,1.2c0.9-0.1,2.8-1.1,3.2-2.3	c0.4-1.1,0.4-2.1,0.3-2.3c-0.1-0.2-0.4-0.3-0.9-0.6s-2.8-1.4-3.2-1.5c-0.4-0.2-0.8-0.2-1.1,0.2c-0.3,0.5-1.2,1.5-1.5,1.9	c-0.3,0.3-0.6,0.4-1,0.1c-0.5-0.2-2-0.7-3.8-2.4c-1.4-1.3-2.4-2.8-2.6-3.3c-0.3-0.5,0-0.7,0.2-1c0.2-0.2,0.5-0.6,0.7-0.8	c0.2-0.3,0.3-0.5,0.5-0.8c2-0.3,0.1-0.6,0-0.8C20.6,19.3,19.7,17,19.3,16z" clipRule="evenodd"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

function MemberHelpOverlay({ onClose }) {
  const handlePhone = () => {
    window.location.href = `tel:${MEMBER_HELP_CONTACT.SUPPORT_NUMBER.replace(/\s/g, "")}`;
  };

  const handleEmail = () => {
    window.location.href = `mailto:${MEMBER_HELP_CONTACT.SUPPORT_EMAIL}`;
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(MEMBER_HELP_TEXT.WHATSAPP_PRETEXT);
    window.location.href = `https://wa.me/${MEMBER_HELP_CONTACT.WHATSAPP_NUMBER.replace(/\s/g, "")}?text=${msg}`;
  };

  return (
    <div className="ProfileMemberHelpOverlay" onClick={onClose} data-aos="fade-in">
      <div className="ProfileMemberHelpModal" onClick={(e) => e.stopPropagation()} data-aos="zoom-in">
        <div className="ProfileMemberHelpHeader">
          <div className="ProfileMemberHelpHeaderIcon">
            <PhoneIcon />
          </div>
          <div className="ProfileMemberHelpHeaderText">
            <h3 className="ProfileMemberHelpTitle">{MEMBER_HELP_TEXT.TITLE}</h3>
            <p className="ProfileMemberHelpSub">{MEMBER_HELP_TEXT.SUBTITLE}</p>
          </div>
          <button className="ProfileMemberHelpClose" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        
        <div className="ProfileMemberHelpGrid">
          <div className="ProfileMemberHelpItem" onClick={handlePhone} data-aos="fade-up" data-aos-delay={100}>
            <div className="ProfileMemberHelpIcon ProfileMemberHelpIcon--phone">
              <PhoneIcon />
            </div>
            <div className="ProfileMemberHelpContent">
              <div className="ProfileMemberHelpLabel">{MEMBER_HELP_TEXT.PHONE_LABEL}</div>
            </div>
            <span className="ProfileMemberHelpAction">
              <ArrowRightIcon />
            </span>
          </div>

          <div className="ProfileMemberHelpItem" onClick={handleEmail} data-aos="fade-up" data-aos-delay={200}>
            <div className="ProfileMemberHelpIcon ProfileMemberHelpIcon--mail">
              <MailIcon />
            </div>
            <div className="ProfileMemberHelpContent">
              <div className="ProfileMemberHelpLabel">{MEMBER_HELP_TEXT.EMAIL_LABEL}</div>
            </div>
            <span className="ProfileMemberHelpAction">
              <ArrowRightIcon />
            </span>
          </div>

          <div className="ProfileMemberHelpItem ProfileMemberHelpItem--whatsapp" onClick={handleWhatsApp} data-aos="fade-up" data-aos-delay={300}>
            <div className="ProfileMemberHelpIcon ProfileMemberHelpIcon--whatsapp">
              <WhatsAppIcon />
            </div>
            <div className="ProfileMemberHelpContent">
              <div className="ProfileMemberHelpLabel">{MEMBER_HELP_TEXT.WHATSAPP_LABEL}</div>
            </div>
            <span className="ProfileMemberHelpAction">
              <ArrowRightIcon />
            </span>
          </div>
        </div>

        <div className="ProfileMemberHelpNote">
          {MEMBER_HELP_TEXT.HELP_NOTE}
        </div>
      </div>
    </div>
  );
}

function fmtDate(dateStr) {
  if (!dateStr) return "\u2014";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function OverviewAppCard({ app, isPending, isDecision, applications, onDelete, user, onGoToDocuments, draftMap, onPay, onMemberHelpClick }) {
  const [trackingOpen, setTrackingOpen] = useState(false);
  const isProcessing = !isPending && !isDecision;
  const count   = app.applicants?.length ?? app.applicantCount ?? 1;
  const govFee  = (app.govFee  || 0) * count;
  const svcFee  = (app.svcFee || 0) * count;
  const protect = app.protectFee || 0;
  const express = app.expressFee || 0;
  const conv    = app.convenienceFee || 0;
  const discount = (app.discountAmount || 0) + (app.walletDisc || 0);
  const total   = govFee + svcFee + protect + conv + express - discount;

  const countryPrefix = (app.countryName || app.destination || "").slice(0, 3).toUpperCase();
  const appId = app.applicationId || app.id || app._id;
  const appIdx = applications.findIndex((a) => (a.applicationId || a.id || a._id) === appId);
  const seqNum = String(appIdx + 1).padStart(3, "0");
  const displayId = `${countryPrefix}${seqNum}`;

  return (
    <div className="ProfileAppCard" data-aos="fade-up">
      {/* Card Header */}
      <div className="ProfileAppCardHeader">
        <div className="ProfileAppCardInfo">
          <div className="ProfileAppCardCountry">
            {app.countryName || app.destination || "\u2014"}</div>
          <div className="ProfileAppCardType">
            {app.visaTypeLabel || app.type || "Visa Application"}
            {app.expressVisa && (
              <span className="ProfileExpressBadge">&#9889; Express</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, alignSelf: "flex-start", marginTop: 2, marginLeft: "auto", marginRight: 4 }}>
          <button
            className="ProfileAppCardDeleteBtn"
            onClick={() => onDelete(app.applicationId || app.id || app._id)}
            title="Remove application"
          >
            {'\u2715'}
          </button>
          <div className="ProfileAppCardUpdatedat">Updates {fmtDate(app.updatedAt || app.appliedAt)}</div>
        </div>
      </div>

      {/* Card Body */}
      <div className="ProfileAppCardBody">
        <div className="ProfileAppCardMetaRow">
          <div className="ProfileAppCardMetaItem">
            <div className="ProfileAppCardMetaLabel">Application ID</div>
            <div className="ProfileAppCardMetaValue ProfileAppCardId">
              {displayId}</div>
          </div>


          {govFee > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Govt Fee</div>
              <div className="ProfileAppCardMetaValue">{formatCurrency(govFee)}</div>
            </div>
          )}
          {svcFee > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Service Fee</div>
              <div className="ProfileAppCardMetaValue">
                {formatCurrency(svcFee)}
                <ServiceFeeTooltip svcFee={app.svcFee || 0} />
              </div>
            </div>
          )}
          {protect > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Visa Protect</div>
              <div className="ProfileAppCardMetaValue">{formatCurrency(protect)}</div>
            </div>
          )}
          {conv > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Convenience Fee</div>
              <div className="ProfileAppCardMetaValue">
                {formatCurrency(conv)}
                <ConvFeeTooltip baseFee={app.convenienceBaseFee || 0} gst={app.convenienceGst || 0} />
              </div>
            </div>
          )}
          {express > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">⚡ Express Fee</div>
              <div className="ProfileAppCardMetaValue ProfileAppCardMetaValue--express">{formatCurrency(express)}</div>
            </div>
          )}
          {total > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Total Fees</div>
              <div className="ProfileAppCardMetaValue ProfileAppCardMetaValue--total">{formatCurrency(total)}</div>
            </div>
          )}
          {discount > 0 && (
            <div className="ProfileAppCardMetaItem">
              <div className="ProfileAppCardMetaLabel">Discount</div>
              <div className="ProfileAppCardMetaValue ProfileAppCardMetaValue--discount">−{formatCurrency(discount)}</div>
            </div>
          )}
          {isDecision && (() => {
            const cfg = getDecisionConfig(app);
            if (!cfg) return null;
            return (
              <div className="ProfileAppCardMetaItem ProfileAppCardMetaItem--full">
                <div className="ProfileAppCardMetaLabel">Decision</div>
                <div className="ProfileAppCardMetaValue">
                  <span className={`ProfileDecisionValue ProfileDecisionValue--${app.decisionStatus}`}>{
                    app.decisionStatus === "approved" ? "✅ Approved" :
                    app.decisionStatus === "rejected" ? "❌ Rejected" : "⏸ On Hold"
                  }</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Card Footer */}
      <div className="ProfileAppCardFooter">
        <button className="ProfileBtnTrack" onClick={() => setTrackingOpen(true)}>
          📍 Track
        </button>
        {isProcessing && (
          <button className="ProfileReceiptBtn" onClick={() => downloadReceiptInner(app, user?.email || "", user?.role || "")}>
            Receipt
          </button>
        )}
        {(isProcessing || isDecision) && (
          <button className="ProfileMemberIconBtn" onClick={() => onMemberHelpClick(true)} title="Need Help? Contact Member Support">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </button>
        )}

        {isPending && !app.paid && (
          <button
            className="ProfileBtnPay ProfileBtnPay--sm"
            style={{ marginLeft: "auto", opacity: user?.profileComplete ? 1 : 0.5 }}
            onClick={() => {
              if (!user?.profileComplete) { alert("Please upload your documents before proceeding to payment."); onGoToDocuments(); return; }
              onPay(app);
            }}
          >
            {draftMap?.[app.applicationId || app.id]
              ? <>Complete</>
              : <>Pay</>}
          </button>
        )}
      </div>

      {trackingOpen && <TrackingModal app={app} onClose={() => setTrackingOpen(false)} />}
    </div>
  );
}

export default function OverviewTab({
  user,
  roleConfig,
  editMode,
  editName,
  editPhone,
  editCity,
  setEditName,
  setEditPhone,
  setEditCity,
  onEdit,
  onSave,
  uploading,
  pendingApplications,
  completedApplications,
  processingApplications,
  decisionApplications,
  applications,
  onPay,
  onDelete,
  onSetDecision,
  onViewAllApplications,
  wallet,
  commissionPct,
  referralCode,
  draftMap,
  uploadedDocs,
  docFiles,
  onGoToDocuments,
}) {
  const navigate = useNavigate();
  const [globalMemberHelpOpen, setGlobalMemberHelpOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const nonBal = wallet?.nonRedeemable?.balance ?? 0;

  const copyReferralCode = useCallback(() => {
    if (!referralCode) return;
    navigator.clipboard.writeText(referralCode).then(() => {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    });
  }, [referralCode]);
  const reBal  = wallet?.redeemable?.balance    ?? 0;
  const roundCustom = (num) => {
    const decimal = num % 1;
    return decimal > 0.5 ? Math.ceil(num) : Math.floor(num);
  };

  const procList = processingApplications ?? completedApplications.filter((a) => !a.decisionStatus);
  const decList  = decisionApplications   ?? completedApplications.filter((a) => !!a.decisionStatus);

  // Show only the most recent 3 of each group on the overview
  const previewPending    = pendingApplications.slice(0, 3);
  const previewProcessing = procList.slice(0, 2);
  const previewDecision   = decList.slice(0, 2);

  return (
    <>
      {/* ── Profile Completion Banner ── */}
      {onGoToDocuments && !user?.profileComplete && (!docFiles || Object.keys(docFiles).length === 0) && (
        <div className="ProfileCompletionBanner" data-aos="fade-up">
          <div className="ProfileCompletionBannerLeft">
            <div className="ProfileCompletionBannerIcon"><Icons.Documents /></div>
            <div>
              <div className="ProfileCompletionBannerTitle">Complete your profile</div>
              <div className="ProfileCompletionBannerSub">Upload your documents to speed up every future visa application</div>
            </div>
          </div>
          <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={onGoToDocuments}>
            <Icons.Upload /> Upload Documents
          </button>
        </div>
      )}

      {/* ── Stats Row ── */}
      <div className={`ProfileStatsRow${user?.role === "individual" ? ' ProfileStatsRow--2cards' : ''}`} data-aos="fade-up">
        {[
          { Icon: Icons.Applications, value: applications.length, label: "Total Applications", color: "var(--Profile-blue)" },
          { Icon: Icons.Check, value: completedApplications.length, label: "Completed", color: "var(--Profile-green)" },
          user?.role !== "individual" && { Icon: Icons.Gift, value: referralCode ? <span className="ProfileStatCode" onClick={copyReferralCode} title="Click to copy referral code">{copiedCode ? "Copied!" : referralCode}<span className="ProfileStatCodeCopy">{Icons.Copy()}</span></span> : <span className="ProfileStatRewardText" title="Pay your visa application fee to unlock your reward code.">Every application brings you closer to your next reward.</span>, label: "Referral Code", color: "var(--Profile-purple-mid)" },
        ].filter(Boolean).map((stat, i) => (
          <StatCard key={i} Icon={stat.Icon} value={stat.value} label={stat.label} color={stat.color} data-aos="fade-up" data-aos-delay={i * 100} />
        ))}
      </div>

      {/* ── Wallet Mini Row ── */}
      <div className="ProfileWalletMiniRow" data-aos="fade-up" data-aos-delay={100}>
        <div className="ProfileWalletMini ProfileWalletMini--non">
          <div className="ProfileWalletMiniLabel">Non-Redeemable Balance</div>
          <div className="ProfileWalletMiniBal">₹{roundCustom(nonBal).toLocaleString("en-IN")}</div>
          <div className="ProfileWalletMiniSub">Welcome bonus · discount only</div>
        </div>
        <div className="ProfileWalletMini ProfileWalletMini--re">
          <div className="ProfileWalletMiniLabel">Redeemable Balance</div>
          <div className="ProfileWalletMiniBal">₹{roundCustom(reBal).toLocaleString("en-IN")}</div>
          <div className="ProfileWalletMiniSub">{commissionPct} referral commission earned</div>
        </div>
      </div>

      {/* ── Application Grid: Pending | Processing | Decision ── */}
      <div className={`ProfileAppsGrid${decList.length === 0 ? ' ProfileAppsGrid--center' : ''}`} data-aos="fade-up" data-aos-delay={200}>
        {/* Pending */}
        <div className="ProfileLandingSection ProfileAppsGridCol">
          <div className="ProfileLandingHeader">
            <div className="ProfileLandingHeaderLeft">
              <div className="ProfileLandingEyebrow">Action Required</div>
              <div className="ProfileLandingTitle">Pending Applications</div>
            </div>
            <div className="ProfileLandingStats">
              <div className="ProfileLasStat">
                <span className="ProfileLasStatNum">{pendingApplications.length}</span>
                <span className="ProfileLasStatLabel">Pending</span>
              </div>
            </div>
            <button className="ProfileBtnBrowse" onClick={() => navigate("/")}>Explore</button>
          </div>
          <div className="ProfileLandingSectionCards">
            {previewPending.length === 0 ? (
              <div className="ProfileAppEmpty">
                {/* <div className="ProfileAppEmptyIcon"><Icons.Clock /></div> */}
                <div className="ProfileAppEmptyTitle">No pending applications</div>
                <div className="ProfileAppEmptySub">Start an application from any country page.</div>
              </div>
            ) : (
              <OverviewAppCard app={previewPending[0]} isPending={true} isDecision={false} applications={applications} onDelete={onDelete} user={user} onGoToDocuments={onGoToDocuments} draftMap={draftMap} onPay={onPay} onMemberHelpClick={setGlobalMemberHelpOpen} />
            )}
            <button className="ProfileBtn ProfileBtnOutline ProfileAppViewMoreBtn" onClick={() => onViewAllApplications("pending")}>
              View More
            </button>
          </div>
        </div>

        {/* Processing */}
        <div className="ProfileLandingSection ProfileAppsGridCol">
          <div className="ProfileLandingHeader">
            <div className="ProfileLandingHeaderLeft">
              <div className="ProfileLandingEyebrow">Embassy Review</div>
              <div className="ProfileLandingTitle">Applications in Processing</div>
            </div>
            <div className="ProfileLandingStats">
              <div className="ProfileLasStat">
                <span className="ProfileLasStatNum">{procList.length}</span>
                <span className="ProfileLasStatLabel">Processing</span>
              </div>
            </div>
          </div>
          <div className="ProfileLandingSectionCards">
            {previewProcessing.length === 0 ? (
              <div className="ProfileAppEmpty">
                {/* <div className="ProfileAppEmptyIcon"><Icons.Clock /></div> */}
                <div className="ProfileAppEmptyTitle">No applications in processing</div>
                <div className="ProfileAppEmptySub">Paid applications will appear here.</div>
              </div>
            ) : (
              <OverviewAppCard app={previewProcessing[0]} isPending={false} isDecision={false} applications={applications} onDelete={onDelete} user={user} onGoToDocuments={onGoToDocuments} draftMap={draftMap} onPay={onPay} onMemberHelpClick={setGlobalMemberHelpOpen} />
            )}
            <button className="ProfileBtn ProfileBtnOutline ProfileAppViewMoreBtn" onClick={() => onViewAllApplications("completed")}>
              View More
            </button>
          </div>
        </div>

        {/* Decision */}
        {decList.length > 0 && (
          <div className="ProfileLandingSection ProfileAppsGridCol">
            <div className="ProfileLandingHeader">
              <div className="ProfileLandingHeaderLeft">
                <div className="ProfileLandingEyebrow">Final Outcome</div>
                <div className="ProfileLandingTitle">Visa Decisions</div>
              </div>
              <div className="ProfileLandingStats">
                <div className="ProfileLasStat">
                  <span className="ProfileLasStatNum">{decList.length}</span>
                  <span className="ProfileLasStatLabel">Decisions</span>
                </div>
              </div>
            </div>
            <div className="ProfileLandingSectionCards">
              {previewDecision.length === 0 ? (
                <div className="ProfileAppEmpty">
                  {/* <div className="ProfileAppEmptyIcon"><Icons.Check /></div> */}
                  <div className="ProfileAppEmptyTitle">No decisions yet</div>
                  <div className="ProfileAppEmptySub">Visa decisions (approved / rejected / on hold) will appear here.</div>
                </div>
              ) : (
                <OverviewAppCard app={previewDecision[0]} isPending={false} isDecision={true} applications={applications} onDelete={onDelete} user={user} onGoToDocuments={onGoToDocuments} draftMap={draftMap} onPay={onPay} onMemberHelpClick={setGlobalMemberHelpOpen} />
              )}
              <button className="ProfileBtn ProfileBtnOutline ProfileAppViewMoreBtn" onClick={() => onViewAllApplications("decision")}>
                View More
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Profile Info Card ── */}
      <div className="ProfileInfoCard" data-aos="fade-up" data-aos-delay={500}>
        <div className="ProfileInfoCardHeader">
          <h3>Profile Information</h3>
          {!editMode ? (
            <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm" onClick={onEdit}>
              <Icons.Edit /> Edit Profile
            </button>
          ) : (
            <div className="ProfileEditBtnRow">
              <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm" onClick={() => onEdit(false)}>
                Cancel
              </button>
              <button
                className="ProfileBtn ProfileBtnPrimary ProfileBtnSm"
                onClick={onSave}
                disabled={uploading}
              >
                {uploading ? <span className="ProfileSpinner" /> : <><Icons.Check /> Save</>}
              </button>
            </div>
          )}
        </div>

        <div className="ProfileInfoGrid">
          <div className="ProfileInfoField">
            <label>Full Name</label>
            {editMode ? (
              <input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Your name" />
            ) : (
              <div className="ProfileFieldValue">{user?.name || "—"}</div>
            )}
          </div>
          <div className="ProfileInfoField">
            <label>Email Address</label>
            <div className="ProfileFieldValue">{user?.email || "—"}</div>
          </div>
          {/* <div className="ProfileInfoField">
            <label>Phone Number</label>
            {editMode ? (
              <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="+91 98765 43210" />
            ) : (
              <div className="ProfileFieldValue">{editPhone || "—"}</div>
            )}
          </div> */}
          {/* <div className="ProfileInfoField">
            <label>City</label>
            {editMode ? (
              <input value={editCity} onChange={(e) => setEditCity(e.target.value)} placeholder="Mumbai" />
            ) : (
              <div className="ProfileFieldValue">{editCity || "—"}</div>
            )}
          </div> */}
          <div className="ProfileInfoField">
            <label>Account Type</label>
            <div className="ProfileFieldValue ProfileFieldValue--role" style={{ '--role-color': roleConfig.color }}>
              {roleConfig.label}
            </div>
          </div>
          <div className="ProfileInfoField">
            <label>User ID</label>
            <div className="ProfileFieldValue">
              <span className="ProfileUidChip">{user?.userId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Global Member Help Overlay */}
      {globalMemberHelpOpen && <MemberHelpOverlay onClose={() => setGlobalMemberHelpOpen(false)} />}
    </>
  );
}